import { Config } from '#lib/constants/config.js';
import { LOCAL_HENI_ARTICLE_HOSTNAME } from '#lib/websites/news/constants/heni-tag.js';

// A trailing dot is the same host to DNS (`heni.com.` === `heni.com`), but not
// string-equal, so it is stripped alongside the `www.` prefix.
const normalizeHostname = (hostname: string) =>
	hostname
		.toLowerCase()
		.replace(/\.$/, '')
		.replace(/^www\./, '');

// Aggregated third-party press is republished on HENI's own site under
// `/news/` (e.g. `${host}/news/article/{slug}`), so a HENI host alone does not
// make an article HENI-authored. Matched on the path segment rather than a
// `startsWith('/news')` so a sibling route such as `/newsroom` is not caught.
const isAggregatedNewsPath = (pathname: string) =>
	pathname === '/news' || pathname.startsWith('/news/');

/**
 * Whether an article url points at HENI-authored content on the environment we
 * are currently running in (the `Config.Domain` host of each environment).
 *
 * Mirrors the backend `isHeni` filter on `getArticles`: the host identifies
 * HENI's own site, but the `/news/` path is excluded because the aggregated
 * third-party press in `HENI_News_Articles` is republished there. Without that
 * exclusion the badge marks someone else's reporting as HENI's own.
 *
 * Compares parsed hostnames rather than the raw string: a `startsWith` on
 * `Config.Domain` both misses protocol/`www.` variants and matches an attacker
 * host such as `https://heni.com.example.com`.
 *
 * `item.url` on a feed item is free text and can be empty, relative or
 * malformed, so anything unparseable is treated as external rather than thrown.
 *
 * The scheme is checked before the host because `new URL` fills in `.hostname`
 * for any scheme carrying an authority: `javascript://heni.com/%0aalert(1)`
 * parses to hostname `heni.com`, and badging that as first-party would put a
 * HENI-branded trust marker on a script payload. Same gate as the sibling
 * `getSectionFromHref`, which reads the same field.
 */
export const isHeniAuthoredUrl = (url: string | null | undefined): boolean => {
	if (!url?.trim()) {
		return false;
	}

	try {
		const parsed = new URL(url);

		if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
			return false;
		}

		if (isAggregatedNewsPath(parsed.pathname)) {
			return false;
		}

		const host = normalizeHostname(parsed.hostname);
		const currentHost = normalizeHostname(new URL(Config.Domain).hostname);

		if (host === currentHost) {
			return true;
		}

		// Local dev only: articles are served from dev while the app runs on
		// localhost, so an exact match would hide the tag on every card.
		return (
			!!LOCAL_HENI_ARTICLE_HOSTNAME &&
			currentHost === 'localhost' &&
			host === LOCAL_HENI_ARTICLE_HOSTNAME
		);
	} catch {
		return false;
	}
};
